import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import SimpleSignupForm from '@/components/simple-signup-form'
import type {PartnerTemplateQuery} from '@/graphql-types'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import {SliceZone} from '@/slices/slice-zone'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {BackgroundWrapper, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PartnerTemplateQuery>

const PartnerTemplate = (props: Props) => {
    const slices = props.data.allPrismicTemplatePartner?.nodes[0].data
    const pageData = props.data.allPrismicPartner?.nodes[0].data
    const predefinedBanner = pageData?.signup_banner

    if (!pageData) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header ctaDestination="partnersform" />

            <Main>
                <Section hideOverflow>
                    <FeatureBlock
                        image={pageData.hero_section[0].hero_image}
                        oversizedImage
                        titleVariant="heading-base"
                    >
                        <TitleWrapper>
                            {hasRichText(pageData.hero_section[0].pre_title.richText) && (
                                <PreTitle>
                                    <RichText
                                        variant="paragraph-base"
                                        content={pageData.hero_section[0].pre_title.richText}
                                    />
                                </PreTitle>
                            )}

                            <Text variant="heading-large" as="h1">
                                {pageData.hero_section[0].title}
                            </Text>
                        </TitleWrapper>

                        <RichText content={pageData.hero_section[0].lead_text.richText} />
                        <SimpleSignupForm
                            {...predefinedBanner.document.data}
                            withBorder
                            formId={getEnvironmentFormId(
                                predefinedBanner.document.data.hubspot_form_id_production,
                                predefinedBanner.document.data.hubspot_form_id_staging
                            )}
                            placeholder={predefinedBanner.document.data.email_input_placeholder}
                            submitText={predefinedBanner.document.data.submit_text}
                            errorText={predefinedBanner.document.data.email_input_error_text}
                        />
                    </FeatureBlock>
                </Section>

                {pageData.social_proof_logos_section && (
                    <TitleWithIconGridSection
                        title={pageData.social_proof_logos_section.document.data.title}
                        items={pageData.social_proof_logos_section.document.data.logos.map(
                            (item) => ({
                                icon: item.icon,
                            })
                        )}
                    />
                )}

                <BackgroundWrapper color="pink">
                    <Section>
                        <FeatureBlock
                            image={pageData.featured_section[0].featured_image}
                            title={pageData.featured_section[0].title}
                            body={pageData.featured_section[0].lead_text.richText}
                            titleVariant="heading-base"
                            mirrored
                        >
                            <Button
                                variant="outline"
                                as={PrismicLink}
                                {...pageData.featured_section[0].cta_link}
                            >
                                {pageData.featured_section[0].cta_label}
                            </Button>
                        </FeatureBlock>
                    </Section>
                </BackgroundWrapper>

                {slices?.body && <SliceZone slices={slices.body} />}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PartnerTemplateQuery>) => {
    const partnerData = data.allPrismicPartner.nodes[0].data
    const pageData = data.allPrismicTemplatePartner.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={partnerData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image.url}
        />
    )
}

const TitleWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
`

const PreTitle = styled.div`
    width: fit-content;
    padding: ${tokens.spacing2} ${tokens.spacing8};
    margin: 0 auto;
    background: ${tokens.pink300};
    border-radius: ${tokens.arc4};

    ${mq.min('sm')} {
        margin: initial;
    }
`

export const query = graphql`
    query PartnerTemplate($uid: String!, $locale: String!) {
        allPrismicPartner(filter: {uid: {eq: $uid}, lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    hero_section {
                        hero_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        pre_title {
                            richText
                        }
                        title
                        lead_text {
                            richText
                        }
                    }
                    signup_banner {
                        document {
                            ...SignupBanner
                        }
                    }
                    social_proof_logos_section {
                        document {
                            ... on PrismicSocialProofLogosSection {
                                data {
                                    title
                                    logos {
                                        icon: logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    featured_section {
                        featured_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        lead_text {
                            richText
                        }
                        cta_label
                        cta_link {
                            ...PrismicLink
                        }
                    }
                }
            }
        }
        allPrismicTemplatePartner(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    body {
                        ...PartnerTemplateGetStarted
                        ...PartnerTemplateFeatureBlock
                        ...PartnerTemplateCustomersLogosPlusHeadline
                        ...PartnerTemplateRatingsPlusHeadline
                        ...PartnerTemplateSocialProof
                        ...PartnerTemplateMediaItemGrid
                    }
                }
            }
        }
    }

    fragment PartnerTemplateGetStarted on PrismicTemplatePartnerDataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            hubspot_form_id_production
            hubspot_form_id_staging
            image {
                alt
                copyright
                url
                dimensions {
                    width
                    height
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment PartnerTemplateFeatureBlock on PrismicTemplatePartnerDataBodyFeatureBlock {
        __typename
        primary {
            title_over_section
            title
            inverse
            background
            headline
            description {
                richText
            }
            pre_cta_links {
                richText
            }
            cta_label
            is_circular_image
            predefined_feature_block {
                document {
                    ... on PrismicFeatureBlock {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            title
                            subtitle
                            body {
                                richText
                            }
                        }
                    }
                }
            }
            cta_link {
                ...PrismicLink
            }
            cta_hash_link
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment PartnerTemplateCustomersLogosPlusHeadline on PrismicTemplatePartnerDataBodyCustomersLogosPlusHeadline {
        __typename
        primary {
            predefined_logos {
                document {
                    ... on PrismicCustomersLogosSection {
                        data {
                            title {
                                richText
                            }
                            logos {
                                icon: logo {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                            }
                        }
                    }
                }
            }
            customer_logos_title {
                richText
            }
        }
        items {
            icon: customer_logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment PartnerTemplateRatingsPlusHeadline on PrismicTemplatePartnerDataBodyRatingsPlusHeadline {
        __typename
        primary {
            ratings_title {
                richText
            }
            ratings_lead_text
            ratings_cta_text
            cta_link {
                ...PrismicLink
            }
            predefined_ratings {
                document {
                    ... on PrismicSocialProofRatingsSection {
                        data {
                            title {
                                richText
                            }
                            lead_text
                            cta_text
                            cta_link {
                                ...PrismicLink
                            }
                            ratings {
                                rating {
                                    document {
                                        ... on PrismicRating {
                                            data {
                                                logo {
                                                    alt
                                                    copyright
                                                    url
                                                    dimensions {
                                                        height
                                                        width
                                                    }
                                                }
                                                stars
                                                rating_number
                                                link
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        items {
            rating {
                document {
                    ... on PrismicRating {
                        data {
                            logo {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            stars
                            rating_number
                            link
                        }
                    }
                }
            }
        }
    }

    fragment PartnerTemplateSocialProof on PrismicTemplatePartnerDataBodySocialProof {
        __typename
        primary {
            title
            inverse
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            quote
            full_name
            person_byline
            predefined_testimonial {
                document {
                    ... on PrismicTestimonial {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            quote
                            attribution_name
                            attribution_title
                        }
                    }
                }
            }
        }
    }

    fragment PartnerTemplateMediaItemGrid on PrismicTemplatePartnerDataBodyMediaItemGrid {
        __typename
        primary {
            title
            layout
            background
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            item_title
            item_description {
                richText
            }
        }
    }
`

export default withPrismicPreview(PartnerTemplate)
